import React from "react";

import { AVATAR_PADRAO, getRegiao, Trilha } from "../TrilhaLudica";

/**
 * O tabuleiro como o aluno vai receber, montado com o que está na tela.
 *
 * Usa o NÚCLEO (`Trilha`), e não o `TrilhaDeAtividade` do aluno, e a diferença
 * não é de estilo: o wrapper do AVA grava no `localStorage` já na primeira
 * renderização — o sorteio das cartas vai para a chave
 * `tl:v6:<userId>:<activityId>`, e numa trilha que ainda não foi salva o
 * `activityId` é `undefined`. Ele também chama `onSincronizar`, que é o POST da
 * experiência. Uma prévia não pode escrever nada, nem no navegador nem no
 * servidor.
 *
 * O núcleo é apresentação pura: recebe o modelo, desenha o cenário da região e
 * devolve cliques. É o mesmo componente que o aluno vê — o cenário, a estrada, os
 * nós e o explorador são os de verdade.
 *
 * O que a prévia NÃO mostra, de propósito: a abertura com o mascote falando, a
 * escolha do explorador e a cerimônia de card. São momentos do aluno, dependem
 * de escolha dele e viriam por cima do tabuleiro — o professor está aqui para
 * conferir o PERCURSO que montou.
 *
 * O PERCURSO APARECE INTEIRO, e é a diferença que o professor vem buscar aqui.
 * Montada como sequencial, a prévia herdava a janela de LEVAS do aluno e desenhava
 * só a leva atual: numa trilha de 10 passos ele via 5, e os outros cinco não
 * estavam atrás da névoa, estavam fora do DOM. Destravada, todos os nós são
 * desenhados e todos trazem o nome — cadeado aqui esconde exatamente o que ele
 * abriu o modal para conferir.
 *
 * O SORTEIO DE EXEMPLO DAS CARTAS saiu junto, e não por descuido: o baú só existe
 * em nó BLOQUEADO (`bauTrancado`, em `nucleo/NoTrilha`), então numa prévia sem
 * cadeado ele não desenha nada — o sorteio viraria cálculo sem efeito. Que a
 * trilha entrega cartas continua dito, em texto, na nota ao pé do tabuleiro.
 *
 * @param {Array}  itens       o `contentList` do ActivityCreator
 * @param {string} regiaoId    `trailRegion` escolhida na tela
 * @param {string} nomeDaTrilha
 */
function PreviaDoAluno({ itens = [], regiaoId, nomeDaTrilha }) {
  const regiao = getRegiao(regiaoId);

  // O modelo neutro que o núcleo consome (contrato em TrilhaLudica/README.md).
  // Zero fases concluídas: é o que o aluno encontra no primeiro acesso.
  //
  // `referencia` é opaca para o núcleo — vai o item inteiro, como o adaptador do
  // aluno faz, e aqui ela não é usada porque o clique não abre nada.
  const trilha = {
    totalFases: itens.length,
    // DESTRAVADA, e só nesta prévia: o professor abre a visão do aluno para
    // conferir o percurso INTEIRO que montou, e a trilha sequencial desenha
    // apenas a leva atual — numa trilha de 10 passos ele via 5, e os outros
    // cinco não estavam escondidos atrás da névoa, estavam fora do DOM.
    //
    // O aluno continua recebendo a sequencialidade de verdade: quem a define é
    // `atividadeParaTrilha`, a partir de `sequential_execution`, e o payload
    // desta tela não muda. O preço é o que o professor ganha: sem cadeado, ele
    // lê o nome de todos os passos em vez de só o do primeiro.
    sequencial: false,
    fases: itens.map((item, indice) => ({
      numero: indice + 1,
      titulo: item.list.name,
      concluida: false,
      referencia: item
    }))
  };

  return (
    <div className="ct-previa">
      <div className="ct-previa__cabecalho">
        <h5 className="ct-previa__titulo">
          {nomeDaTrilha || "Trilha sem nome"}
        </h5>
        <span className="ct-previa__regiao">
          {`${regiao.nome} — ${regiao.bioma} · ${regiao.mascote.nome}`}
        </span>
      </div>

      <Trilha
        trilha={trilha}
        // O clique não abre nada: numa prévia, abrir o item levaria o professor
        // para fora da tela de criação, com o formulário por salvar.
        onAbrirFase={() => {}}
        avatar={AVATAR_PADRAO.asset}
        regiao={regiao}
        mensagemVazia="Escolha os objetos da trilha para ver o percurso do aluno."
      />

      <small className="text-muted ct-previa__nota">
        Aqui os passos aparecem todos abertos, para você conferir o percurso
        inteiro; o aluno os destrava um a um. Ele também escolhe o próprio
        explorador ao abrir a trilha, e recebe as cartas da região ao vencer as
        fases — cada aluno encontra as cartas em passos diferentes.
      </small>
    </div>
  );
}

export default PreviaDoAluno;
